import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import OrderAddOnService from '../../../../../services/orders/odrerAddOn';
import ErrorService from '../../../../../services/general/error';

// props

// components
import { Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import GeneralService from '../../../../../services/general';
import { PPNPercentageOptions, PPhPercentageOptions } from '../../../../../manifests/taxPercentage';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import PricingService from '../../../../../services/general/pricing';
import PermissionsService from '../../../../../services/permissions';
import { IOrderAddonResourceProps } from '../../../../../props/orders/orderAddon';
import Details from '../../../../typography/details';
import { IQuantityUnitResourceShortProps } from '../../../../../props/general/quantityUnit';
import QuantityUnitService from '../../../../../services/general/quantityUnit';

interface IOrderAdditionalChargeFormProps {
    orderId: string;
    additionalChargeId?: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    name: string;
    notes: string;
    price: string;
    ppn: string;
    pph: string;
    includePPn?: boolean;
    includePPh?: boolean;
    ppnPercentage: string;
    pphPercentage: string;
    finalPrice: string;
    nettPrice: string;
    invoicePrice: string;
    priceType: string;
    quantityAmount: string;
    quantityUnit?: IQuantityUnitResourceShortProps;
}

const OrderAdditionalChargeForm: React.FC<IOrderAdditionalChargeFormProps> = (props: IOrderAdditionalChargeFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [updatePrice, setUpdatePrice] = React.useState<boolean>(false);
    const [addon, setAddon] = React.useState<IOrderAddonResourceProps | undefined>();
    const [data, setData] = React.useState<FormDataProps>({
        name: "",
        notes: "",
        price: "",
        ppn: "0",
        pph: "0",
        ppnPercentage: "0",
        pphPercentage: "0",
        finalPrice: "0",
        nettPrice: "0",
        invoicePrice: "0",
        priceType: 'lumpsum',
        quantityAmount: '1'
    });
    const [quantityUnits, setQuantityUnits] = React.useState<IQuantityUnitResourceShortProps[]>([]);
    const [error, setError] = React.useState<any>({});
    const mode: 'create' | 'update' = props.additionalChargeId === undefined ? 'create' : 'update';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        if (props.additionalChargeId) {
            const _additionalCharge = await OrderAddOnService.get(props.orderId, props.additionalChargeId);

            setData({
                name: _additionalCharge.name,
                notes: _additionalCharge.notes || "",
                price: Number(_additionalCharge.price) + "",
                ppn: Number(_additionalCharge.ppn) + "",
                pph: Number(_additionalCharge.pph) + "",
                ppnPercentage: Number(_additionalCharge.ppnPercentage) + "",
                pphPercentage: Number(_additionalCharge.pphPercentage) + "",
                finalPrice: Number(_additionalCharge.totalPrice) + "",
                nettPrice: Number(_additionalCharge.nettPrice) + "",
                invoicePrice: Number(_additionalCharge.invoicePrice) + "",
                includePPn: _additionalCharge.includePPn,
                includePPh: _additionalCharge.includePPh,
                priceType: _additionalCharge.priceType,
                quantityAmount: Number(_additionalCharge.quantityAmount) + "",
                quantityUnit: _additionalCharge.quantityUnit
            });
            setAddon(_additionalCharge);
        }

        const _quantityUnits = await QuantityUnitService.retrieve();
        setQuantityUnits(_quantityUnits);

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.name || error.notes || error.price || error.quantityAmount) {
            return true;
        } else if (data.name === '' || data.price === '' || data.quantityAmount === '') {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, notes, price, ppn, pph, quantityAmount, quantityUnit, priceType, includePPn, includePPh, ppnPercentage, pphPercentage, finalPrice, nettPrice, invoicePrice } = data;

            // create form data
            let fd = new FormData();
            fd.append("name", name);
            fd.append("notes", notes);

            if (updatePrice) {
                fd.append("totalPrice", finalPrice);
                fd.append("finalPrice", finalPrice);
                fd.append("nettPrice", nettPrice);
                fd.append("invoicePrice", invoicePrice);
                fd.append("price", price);
                fd.append("ppn", ppn);
                fd.append("pph", pph);
                fd.append("includePPn", includePPn + "");
                fd.append("includePPh", includePPh + "");
                fd.append("ppnPercentage", ppnPercentage + "");
                fd.append("pphPercentage", pphPercentage + "");

                fd.append('priceType', priceType);
                fd.append('quantityAmount', quantityAmount);
                fd.append('quantityUnitId', quantityUnit?.id || "");

                if (addon?.invoiceId) {
                    fd.append("updateInvoice", "1");
                }
            }

            if (props.additionalChargeId === undefined) {
                await OrderAddOnService.new(props.orderId, fd);
            } else {
                await OrderAddOnService.update(props.orderId, props.additionalChargeId, fd);
            }
            banner.add({
                key: mode + '_order_additional_charge_success',
                variant: 'success',
                icon: faCheck,
                text: `Additional charge "${name}" ${mode === 'create' ? 'created' : 'updated'} successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
            banner.add({
                key: mode + '_order_additional_charge_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Gagal ${mode === 'create' ? 'menambahkan' : 'mengubah'} additional charge: ` + ErrorService.getMessage(e)
            });
        }
    }

    const calculateAllPrice = (_data: FormDataProps) => {
        const pricing = PricingService.order.calculate(_data, _data.priceType);
        setData({ ..._data, ...pricing });
    }

    return <Panel headerText={mode === 'create' ? 'Create Additional Charge' : 'Update Additional Charge'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating additional charge ..." : "Updating additional charge ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <Stack.Item>
                        <TextField label={"Name"}
                            required={true}
                            value={data.name}
                            onChange={(evt, value) => {
                                data.name = value || "";
                                
                                const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 100});
                                error.name = validation.message;

                                setData({...data});
                                setError({...error});
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Notes"}
                            multiline
                            rows={5}
                            autoAdjustHeight
                            resizable={false}
                            value={data.notes}
                            onChange={(evt, value) => {
                                data.notes = value || "";
                                
                                const validation = ValidationService.combination(value, ['limit'], {maxChars: 100});
                                error.notes = validation.message;

                                setData({...data});
                                setError({...error});
                            }}
                            errorMessage={error.notes}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{childrenGap: 10}}>
                        <Stack styles={{root: {width: '100%'}}}>
                            <TextField label={"Quantity"}
                                value={data.quantityAmount}
                                onChange={(evt, value) => {
                                    if (!isNaN(Number(value))) {
                                        data.quantityAmount = value || "";

                                        const validation = ValidationService.combination(value, ['required', 'limit', 'number'], { maxChars: 15, min: 1 });
                                        error.quantityAmount = validation.message;

                                        setData({ ...data });
                                        setError({ ...error });
                                        calculateAllPrice(data);
                                    }
                                }}
                                errorMessage={error.quantityAmount}
                                disabled={submitting} />
                        </Stack>
                        <Stack.Item styles={{ root: { width: 200 } }}>
                            <Dropdown label={" "}
                                selectedKey={data.quantityUnit?.id}
                                options={quantityUnits.map((unit) => { return { key: unit.id, text: unit.name } })}
                                styles={{root: {marginTop: 3}}}
                                onChange={(evt, opt) => {
                                    const selectedId = opt ? opt.key as string : undefined;
                                    data.quantityUnit = quantityUnits.find((unit) => unit.id === selectedId);
    
                                    setData({ ...data });
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Checkbox label={'Update price'}
                        checked={updatePrice}
                        disabled={(addon?.invoiceId || "").trim() !== "" && !PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions)}
                        onChange={(ev, checked) => setUpdatePrice(checked || false)} />
                    {addon?.invoiceId && updatePrice ? <Text className={'color-yellow'}>!!! WARNING !!! This additional charge already associated with an invoice, updating this additional charge price will also update invoice amount.</Text> : null}
                    <Stack.Item>
                        <Dropdown label={"Price type"}
                            selectedKey={data.priceType}
                            options={[
                                { key: 'lumpsum', text: 'Lump sum' },
                                { key: 'perquantity', text: 'Per quantity' }
                            ]}
                            onChange={(evt, opt) => {
                                data.priceType = opt?.key as string;
                                setData({ ...data });
                                calculateAllPrice(data);
                            }}
                            disabled={submitting || !updatePrice} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={data.priceType === 'perquantity' ? "Price per Quantity" : "Total Price"}
                            value={data.price}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.price = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.price = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            suffix={data.price !== "" ? GeneralService.convertNumberToWords(Number(data.price)) : undefined}
                            errorMessage={error.price}
                            disabled={submitting || !updatePrice} />
                    </Stack.Item>
                    {data.priceType === 'perquantity' ? <Stack>
                        <Label size={'small'}>Total Price</Label>
                        <Text>Rp. {GeneralService.getNumberWithSeparator(Number(data.price) * Number(data.quantityAmount))}</Text>
                    </Stack> : null}
                    <Stack tokens={{ childrenGap: 20 }} horizontal>
                        <Stack.Item>
                            <Checkbox checked={data.includePPn}
                                label='Include PPn'
                                disabled={submitting || !updatePrice}
                                onChange={(ev, checked) => {
                                    data.includePPn = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                        <Stack.Item>
                            <Checkbox checked={data.includePPh}
                                disabled={submitting || !updatePrice}
                                label='Include PPh'
                                onChange={(ev, checked) => {
                                    data.includePPh = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPn</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPNPercentageOptions}
                                    selectedKey={data.ppnPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.ppnPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.ppn}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.ppn = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.ppn = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.ppn}
                                    disabled={submitting || data.ppnPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPh</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPhPercentageOptions}
                                    selectedKey={data.pphPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.pph = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.pphPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting || !updatePrice}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.pph}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.pph = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.pph = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.pph}
                                    disabled={submitting || data.pphPercentage !== 'custom' || !updatePrice} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Total price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.finalPrice)}`} style={{width: '50%'}} />
                        <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.invoicePrice)}`} style={{width: '50%'}} />
                    </Stack>
                </> : null
            }
        </Stack>
    </Panel>
};

export default OrderAdditionalChargeForm;
